import React, { useEffect, useState } from "react";

import Customize from "../../partials/settings/customize/index.jsx";
import { useDispatch, useSelector } from "react-redux";
import PremiumPaywall from "../../components/PremiumPaywall.jsx";
import { DangerButton, PrimaryButton } from "../../components/Buttons.jsx";
import { updateMothershipInfo, uploadFile } from "../../api/index.js";

import {
  getMerchantCustomerJob,
  submitCustomDomain,
} from "../../api/settings.js";
import { addToast, getMerchants } from "../../store/app/action.js";
import Image from "../../images/user-avatar-80.png";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { convertToRaw, ContentState, EditorState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import ToggleSwitch from "../../components/ToggleSwitch.jsx";
import {
  PAYMENT_LOGO_OPTIONS,
  buildMerchantPaymentLogoPayload,
  merchantPaymentLogoToUi,
  paymentLogoExplicitBrandIds,
  stablePaymentLogoJson,
} from "../../utils/paymentLogos.js";

function Mothership() {
  const CustomDomainStatusDot = () => {
    let color;
    let copy;

    if (currentJob) {
      switch (currentJob.status) {
        case "inactive":
          color = "bg-slate-400";
          copy = "No custom domain connected";
          break;
        case "pending":
          color = "bg-yellow-400";
          copy = "Custom domain pending activation, check back later";
          break;
        case "active":
        case "completed":
          color = "bg-green-400";
          copy = "Custom domain connected";
          break;
        case "error":
          color = "bg-red-400";
          copy =
            "Error connecting custom domain, enter a new domain to try again";
          break;
        default:
          color = "bg-slate-400";
          copy = "No custom domain connected";
      }
      return (
        <>
          <span
            className={`h-3 w-3 rounded-full ${color} inline-block`}
            title={status}
          ></span>
          <span className="ml-2">{copy}</span>
        </>
      );
    } else if (selectedMerchant?.custom_direct_web_urls?.length) {
      return (
        <>
          <span
            className={`h-3 w-3 rounded-full bg-green-400 inline-block`}
          ></span>
          <span className="ml-2">Custom domain connected</span>
        </>
      );
    } else {
      return (
        <>
          <span
            className={`h-3 w-3 rounded-full bg-slate-400 inline-block`}
          ></span>
          <span className="ml-2">No custom domain connected</span>
        </>
      );
    }
  };
  const { selectedMerchant } = useSelector((state) => state.app);
  const [loading, setLoading] = useState(false);
  const iconUploadRef = React.createRef();
  const iconUrlRef = React.createRef();
  const iconDisplayRef = React.createRef();
  const bannerUploadRef = React.createRef();
  const bannerUrlRef = React.createRef();
  const bannerDisplayRef = React.createRef();

  const [submittingCustomDomain, setSubmittingCustomDomain] = useState(false);
  const [submittedCustomDomain, setSubmittedCustomDomain] = useState(false);
  const [customDomain, setCustomDomain] = useState("");
  const [currentJob, setCurrentJob] = useState(null);
  const dispatch = useDispatch();
  const [customDomainCollapsed, setCustomDomainCollapsed] = useState(false);
  const [sitemapCollapsed, setSitemapCollapsed] = useState(false);
  const [editorState, setEditorState] = useState(() => {
    return EditorState.createEmpty();
  });
  const [amSoDirty, setAmSoDirty] = useState(false);
  const [hideMothershipPaymentLogos, setHideMothershipPaymentLogos] =
    useState(false);
  const [selectedMothershipPaymentLogoIds, setSelectedMothershipPaymentLogoIds] =
    useState([]);

  const formRef = React.createRef();

  const toggleMothershipPaymentLogoId = React.useCallback((id) => {
    setSelectedMothershipPaymentLogoIds((prev) => {
      if (!prev.includes(id)) {
        return [...prev, id];
      }
      return prev.filter((x) => x !== id);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const keysToCheck = [];
    const formData = new FormData(e.target);
    const data = {
      icon: formData.get("icon"),
      web_banners: [formData.get("web_banners")],
      description_html: draftToHtml(
        convertToRaw(editorState.getCurrentContent())
      )
        .replace("<p></p>", "")
        .replace("\\n", ""),
      payment_logos: buildMerchantPaymentLogoPayload(
        hideMothershipPaymentLogos,
        selectedMothershipPaymentLogoIds
      ),
    };

    if (!keysToCheck.every((key) => data[key] !== undefined)) {
      // log each field name that's not filled out
      dispatch(
        addToast({
          type: "error",
          message: "Please fill out all fields",
          timer: 5,
        })
      );
      return;
    }

    try {
      await updateMothershipInfo({ basic: data });
      dispatch(
        addToast({ type: "success", message: "Mothership updated", timer: 5 })
      );
      dispatch(getMerchants());
      setAmSoDirty(false);
    } catch (e) {
      let message = "please try again";
      try {
        const serverMessage = e.response?.data?.error;
        if (serverMessage) {
          message = serverMessage;
        }
      } catch {}

      dispatch(
        addToast({
          type: "error",
          message: "Error updating store " + message,
          timer: 5,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const onEditorStateChange = (editorState) => {
    setEditorState(editorState);
    checkDirty();
  };
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    let checks = {};
    if (e.target.name === "icon") {
      checks = {
        minWidth: 100,
        width: 100,
        height: 100,
        minAspectRatio: 1,
        maxAspectRatio: 1.25,
      };
    } else if (e.target.name === "banner") {
      checks = {
        minHeight: 300,
        height: 300,
        minAspectRatio: 1.5,
        maxAspectRatio: 3,
      };
    }

    if (!file.type.startsWith("image/")) {
      dispatch(
        addToast({
          type: "error",
          message: "Please upload a valid image file",
          timer: 5,
        })
      );
      return;
    }
    const img = document.createElement("img");
    img.onload = () => {
      if (checks.minWidth) {
        if (img.width < checks.minWidth) {
          dispatch(
            addToast({
              type: "error",
              message:
                "Please upload an image at least " +
                checks.minWidth +
                "px wide",
              timer: 10,
            })
          );
          return;
        }
      }
      if (checks.minHeight) {
        if (img.height < checks.minHeight) {
          dispatch(
            addToast({
              type: "error",
              message:
                "Please upload an image at least " +
                checks.minHeight +
                "px tall",
              timer: 10,
            })
          );
          return;
        }
      }
      // Check aspect ratio
      if (checks.minAspectRatio || checks.maxAspectRatio) {
        if (
          img.width / img.height < checks.minAspectRatio ||
          img.width / img.height > checks.maxAspectRatio
        ) {
          dispatch(
            addToast({
              type: "error",
              message:
                "Please upload an image an aspect ratio between " +
                checks.minAspectRatio +
                ":1 and " +
                checks.maxAspectRatio +
                ":1",
              timer: 10,
            })
          );
          return;
        }
      }

      // Resize image
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (checks.width && checks.height) {
        canvas.width = checks.width;
        canvas.height = checks.height;
      } else if (checks.width && !checks.height) {
        canvas.width = checks.width;
        canvas.height = img.height * (checks.width / img.width);
      } else if (!checks.width && checks.height) {
        canvas.height = checks.height;
        canvas.width = img.width * (checks.height / img.height);
      } else {
        canvas.width = img.width;
        canvas.height = img.height;
      }
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob and upload
      canvas.toBlob(async (blob) => {
        try {
          try {
            const response = await uploadFile(blob);
            switch (e.target.name) {
              case "icon":
                iconUrlRef.current.value = response.url;
                iconDisplayRef.current.src = response.url;
                break;
              case "banner":
                bannerUrlRef.current.value = response.url;
                bannerDisplayRef.current.src = response.url;
                break;
            }
            checkDirty();
          } catch (e) {}
        } catch (error) {
        }
      }, file.type);
    };

    // Read the file as a URL to create an image
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };
  const checkDirty = () => {
    if (!selectedMerchant || !formRef.current) {
      return;
    }
    const keysToCheck = [
      "icon",
      "web_banners",
    ];
    const data = new FormData(formRef.current);
    const dirty = keysToCheck.some((key) => {
      let original = selectedMerchant[key] || "";
      let updated = data.get(key) || "";
      if (key === "web_banners") {
        original = selectedMerchant.web_banners?.[0];
        updated = data.get(key);
      }
      return original !== updated;
    });

    const descriptionDirty =
      draftToHtml(convertToRaw(editorState.getCurrentContent()))
        .replace("<p></p>", "")
        .replace("\\n", "") !== selectedMerchant.description_html;
    const paymentPayload = buildMerchantPaymentLogoPayload(
      hideMothershipPaymentLogos,
      selectedMothershipPaymentLogoIds
    );
    const paymentLogoDirty =
      stablePaymentLogoJson(paymentPayload) !==
      stablePaymentLogoJson(selectedMerchant?.payment_logos);
    setAmSoDirty(dirty || descriptionDirty || paymentLogoDirty);
  };

  useEffect(() => {
    getMerchantCustomerJob()
      .then((res) => {
        if (res.job) {
          setCurrentJob(res.job);
          setCustomDomain(res.job.domain);
        } else {
          if (selectedMerchant?.custom_direct_web_urls?.length) {
            setCustomDomain(selectedMerchant.custom_direct_web_urls[0]);
          }
        }
      })
      .catch(() => {
        if (selectedMerchant?.custom_direct_web_urls?.length) {
          setCustomDomain(selectedMerchant.custom_direct_web_urls[0]);
        }
      });
    const contentBlock = htmlToDraft(selectedMerchant?.description_html || "");
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(
        contentBlock.contentBlocks
      );
      const editorState = EditorState.createWithContent(contentState);
      setEditorState(editorState);
    } else {
      setEditorState(EditorState.createEmpty());
    }
    const ui = merchantPaymentLogoToUi(selectedMerchant?.payment_logos);
    setHideMothershipPaymentLogos(ui.hidden);
    setSelectedMothershipPaymentLogoIds(ui.selectedIds);
  }, [selectedMerchant]);

  useEffect(() => {
    if (!formRef.current || !selectedMerchant) return;
    checkDirty();
  }, [hideMothershipPaymentLogos, selectedMothershipPaymentLogoIds]);

  if (!selectedMerchant) {
    return null;
  }
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      {/* Page header */}
      <div className="mb-8">
        {/* Title */}
        <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
          Mothership 
        </h1>
      </div>

      <input
        type="file"
        accept="image/*"
        ref={iconUploadRef}
        onChange={handleFileChange}
        name="icon"
        style={{ display: "none" }} // Hide the file input
      />
      <input
        type="file"
        accept="image/*"
        ref={bannerUploadRef}
        onChange={handleFileChange}
        name="banner"
        style={{ display: "none" }} // Hide the file input
      />
      {/* Content */}
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm mb-8">
        <div className="flex flex-col md:flex-row md:-mr-px">
          <div className="grow">
            {/* Panel body */}
            <form onSubmit={submit} onChange={checkDirty} ref={formRef}>
              <div className="p-6 space-y-6">
                <section>
                  <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                    Mothership Logo
                  </h2>
                  <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
                    <div className="mr-4">
                      <img
                        className="w-20 h-20"
                        src={selectedMerchant.icon || Image}
                        ref={iconDisplayRef}
                        width="80"
                        height="80"
                        alt="User upload"
                      />
                      <input
                        name="icon"
                        ref={iconUrlRef}
                        className="form-input w-full"
                        type="text"
                        style={{ display: "none" }}
                        defaultValue={selectedMerchant.icon}
                      />
                    </div>
                    <PrimaryButton
                      size={"sm"}
                      onClick={(e) => {
                        e.preventDefault();
                        iconUploadRef.current.click();
                      }}
                    >
                      Change
                    </PrimaryButton>
                  </div>
                </section>
                <section>
                  <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                    Header Image
                  </h2>
                  <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
                    <div className="mr-4">
                      <img
                        className="w-80 h-40"
                        ref={bannerDisplayRef}
                        src={selectedMerchant.web_banners?.[0] || Image}
                        width="160"
                        height="80"
                        alt="User upload"
                      />
                      <input
                        name="web_banners"
                        ref={bannerUrlRef}
                        className="form-input w-full"
                        type="text"
                        style={{ display: "none" }}
                        defaultValue={selectedMerchant.web_banners?.[0]}
                      />
                    </div>
                    <DangerButton
                      size={"sm"}
                      onClick={(e) => {
                        e.preventDefault();
                        bannerUrlRef.current.value = "";
                        bannerDisplayRef.current.src = Image;
                      }}
                    >
                      Remove
                    </DangerButton>
                    <PrimaryButton
                      size={"sm"}
                      onClick={(e) => {
                        e.preventDefault();
                        bannerUploadRef.current.click();
                      }}
                    >
                      Change
                    </PrimaryButton>
                  </div>
                </section>
                <section>
                  <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                    Payment logos
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 max-w-2xl">
                    Choose which card networks appear in the footer for your main
                    site.
                  </p>
                  <ToggleSwitch
                    checked={!hideMothershipPaymentLogos}
                    onChange={(showLogos) => {
                      if (!showLogos) {
                        setHideMothershipPaymentLogos(true);
                        return;
                      }
                      setHideMothershipPaymentLogos(false);
                      setSelectedMothershipPaymentLogoIds(
                        paymentLogoExplicitBrandIds(
                          selectedMerchant?.payment_logos
                        )
                      );
                    }}
                    name="mothership_show_payment_logos"
                    onLabel="Show payment method logos in footer"
                    offLabel="Show payment method logos in footer"
                  />
                  {!hideMothershipPaymentLogos && (
                    <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3">
                      {PAYMENT_LOGO_OPTIONS.map(({ id, label }) => (
                        <label
                          key={id}
                          className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="form-checkbox text-indigo-500"
                            checked={selectedMothershipPaymentLogoIds.includes(id)}
                            onChange={() => toggleMothershipPaymentLogoId(id)}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  )}
                </section>
                <section>
                  <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
                    Description
                  </h2>
                  <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
                    <div className="w-full">
                      {/*   editor */}
                      <Editor
                        editorState={editorState}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="w-3/4  h-48"
                        editorClassName="form-input w-full"
                        onEditorStateChange={onEditorStateChange}
                        toolbar={{
                          options: ["inline", "list"],
                          inline: {
                            inDropdown: false,
                            options: [
                              "bold",
                              "italic",
                              "underline",
                              "superscript",
                              "subscript",
                            ],
                          },
                          list: { inDropdown: false },
                          textAlign: { inDropdown: false, options: [] },
                          link: {
                            inDropdown: false,
                            options: [],
                            component: null,
                          },
                          image: {
                            uploadEnabled: false,
                            options: [],
                            component: null,
                          },
                          embedded: { options: [], component: null },
                          remove: {
                            className: undefined,
                            component: undefined,
                          },

                          history: {
                            inDropdown: false,
                            options: [],
                            component: null,
                          },
                        }}
                      />
                    </div>
                  </div>
                </section>
                
                <div className="flex self-end pt-8">
                  <PrimaryButton
                    type="submit"
                    disabled={!amSoDirty}
                    loading={loading}
                  >
                    Save Changes
                  </PrimaryButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm mb-8">
        <div className="flex flex-col md:flex-row md:-mr-px relative">
          <PremiumPaywall blockScroll={false} requiresAddon={['seo-boost']} requiresPlan={['growth']} />
          <div className="grow">
            {/* Panel body */}
            <div className="p-6 space-y-6">
              <div>
                <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-4">
                  Custom Domain
                  <div
                    className={
                      "transition-all duration-300 inline-block ml-2 cursor-pointer " +
                      (customDomainCollapsed ? "" : "scale-y-[-1]")
                    }
                    onClick={() =>
                      setCustomDomainCollapsed(!customDomainCollapsed)
                    }
                  >
                    <svg
                      className="w-3 h-3 shrink-0 ml-1 fill-current text-slate-400"
                      viewBox="0 0 12 12"
                    >
                      <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                    </svg>
                  </div>
                </h2>
                {/*animate*/}
                <div
                  className={`overflow-hidden transition-all duration-700 ${
                    customDomainCollapsed ? "h-0" : "h-auto"
                  }`}
                >
                  <div className="text-sm">
                    <p className="mb-4">
                      Connect your domain or subdomain to Saucey Direct and give
                      your store a fully branded web presence. Instead of using
                      a Saucey Direct URL (e.g.,
                      direct.saucey.com/shop/mystore), you can use your own
                      custom domain (e.g., shopmystore.com), creating a seamless
                      and professional shopping experience for your customers.
                    </p>
                    <p>
                      For more information on how to connect your domain,{" "}
                      <a
                        href="https://intercom.help/sauceydirect/en/articles/10860223-using-a-custom-domain-for-your-saucey-direct-store"
                        target="_blank"
                        className="text-indigo-500 underline cursor-pointer"
                      >
                        click here
                      </a>
                      .
                    </p>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium mb-1">
                      Custom domain / subdomain
                    </label>
                    <div className="flex gap-2 w-96">
                      <input
                        type="text"
                        className="form-input flex-grow"
                        placeholder="shopmystore.com"
                        value={customDomain}
                        onChange={(e) => setCustomDomain(e.target.value)}
                      />
                      <PrimaryButton
                        loading={submittingCustomDomain}
                        success={submittedCustomDomain}
                        onClick={async () => {
                          const replacing =
                            Boolean(
                              selectedMerchant?.custom_direct_web_urls?.length
                            ) || !currentJob?.closed;
                          setSubmittingCustomDomain(true);
                          try {
                            const res = await submitCustomDomain({
                              type: "mothership",
                              domain: customDomain,
                            });
                            dispatch(
                              addToast({
                                type: "success",
                                timer: 5,
                                message: `Custom domain is being processed, check back later.`,
                              })
                            );
                            setSubmittedCustomDomain(true);
                            setCurrentJob(res.job);
                            setTimeout(() => {
                              setSubmittedCustomDomain(false);
                            }, 5000);
                          } catch (e) {
                            let message = "An error occurred";
                            try {
                              const serverMessage = e.response?.data?.message;
                              if (serverMessage) {
                                message = serverMessage;
                              }
                            } catch {}
                            message += ", domain is not submitted.";
                            dispatch(
                              addToast({
                                type: "error",
                                message: message,
                              })
                            );
                          } finally {
                            setSubmittingCustomDomain(false);
                          }
                        }}
                      >
                        {Boolean(
                          selectedMerchant?.custom_direct_web_urls?.length
                        ) || !currentJob?.closed
                          ? "Replace"
                          : "Save"}
                      </PrimaryButton>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
                        <CustomDomainStatusDot />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm mb-8">
        <div className="flex flex-col md:flex-row md:-mr-px relative">
          <PremiumPaywall blockScroll={false} requiresAddon={['seo-boost']} requiresPlan={['growth']} />
          <div className="p-6 space-y-6">
            <div>
              <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-4">
                Sitemap
                <div
                  className={
                    "transition-all duration-300 inline-block ml-2 cursor-pointer " +
                    (sitemapCollapsed ? "" : "scale-y-[-1]")
                  }
                  onClick={() => setSitemapCollapsed(!sitemapCollapsed)}
                >
                  <svg
                    className="w-3 h-3 shrink-0 ml-1 fill-current text-slate-400"
                    viewBox="0 0 12 12"
                  >
                    <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                  </svg>
                </div>
              </h2>
              {/*animate*/}
              <div
                className={`overflow-hidden transition-all duration-700 ${
                  sitemapCollapsed ? "h-0" : "h-auto"
                }`}
              >
                <div className="text-sm">
                  <p className="mb-4">
                    When you connect a custom domain, we automatically generate
                    a sitemap and submit it to major search engines like Google,
                    Bing, and Yahoo. This helps search engines find and index
                    your site more quickly, improving its visibility in search
                    results. By publishing a sitemap to your Saucey Direct
                    store, we ensure your website stays discoverable, making it
                    easier for potential visitors to find you online.
                  </p>
                  <p>
                    We continuously monitor your website for updates, scanning
                    for changes in products, pages, and other content once a
                    day. Whenever we detect an update, we automatically refresh
                    your sitemap and notify search engines to ensure your latest
                    content is indexed as quickly as possible.
                  </p>
                </div>
                {/*  list some search engines, and have green dot next to them*/}
                {["Google", "Bing", "Yahoo", "Yandex"].map((engine) => (
                  <div key={engine} className="flex items-center mt-4 text-sm">
                    <div
                      className={`h-3 w-3 rounded-full inline-block ${
                        selectedMerchant?.custom_direct_web_urls?.length
                          ? "bg-green-400"
                          : "bg-red-500"
                      }`}
                    ></div>
                    <span className="ml-2">
                      {engine}{" "}
                      {!selectedMerchant?.custom_direct_web_urls?.length &&
                        "- Requires active custom domain"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm mb-8">
        <div className="flex flex-col md:flex-row md:-mr-px relative">
          <Customize type='mothership'/>
          <PremiumPaywall blockScroll={false} requiresAddon={['seo-boost']} requiresPlan={['growth']} />
        </div>
      </div>

    </div>
  );
}

export default Mothership;
